import React, { useRef, useState, useCallback } from 'react';
import FadeIn from '../shared/FadeIn';
import WordFlip from '../shared/WordFlip';
import CTASection from '../shared/CTASection';
import { getHomeFeatures, getHomeBottomFeature } from '../../data/homeFeatures';
import { getProductFeatures } from '../../data/productFeatures';
import { useT, useLang } from '../../i18n/LanguageContext';
import strings from './HomePage.i18n';
import './HomePage.css';
import SpotlightEffect from '../shared/SpotlightEffect';
import GSAPReveal from '../shared/GSAPReveal';
import LogoMarquee from '../shared/LogoMarquee';
import TestimonialCarousel from '../shared/TestimonialCarousel';
import CountUp from '../shared/CountUp';
import Particles from '../shared/Particles';

export default function HomePage({ onNavigate }) {
  const gridRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const t = useT(strings);
  const { lang } = useLang();
  const homeFeatures = getHomeFeatures(lang);
  const homeBottomFeature = getHomeBottomFeature(lang);
  const productFeatures = getProductFeatures(lang);

  const handleGridScroll = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setScrollProgress(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
  }, []);
  const handleRequestDemo = () => {
    window.open(
      'https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20started%20with%20SAPOT%20AI',
      '_blank'
    );
  };

  const handleWatchDemo = () => {
    window.open('https://www.youtube.com/watch?v=Te2X95CDP4s', '_blank');
  };

  return (
    <div className="home-page">
      {/* ======== P1: Hero ======== */}
      <section className="home-hero gradient-hero">
        <div className="section">
          <FadeIn>
            <h1>
              <WordFlip words={t.wordFlipWords} />{t.heroHeadlineSuffix}
            </h1>
          </FadeIn>
          <FadeIn delay={100}>
            <p className="hero-subtitle">
              {t.heroSubtitle}
            </p>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="hero-actions">
              <button className="btn-primary" onClick={handleRequestDemo}>
                {t.requestDemo}
              </button>
              <button className="btn-secondary" onClick={handleWatchDemo}>
                {t.watchDemo}
              </button>
            </div>
          </FadeIn>
          <FadeIn delay={300}>
            <div className="hero-screenshot">
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="hero-video"
              >
                <source src={process.env.PUBLIC_URL + '/videos/demo.mp4'} type="video/mp4" />
              </video>
            </div>
          </FadeIn>
          <LogoMarquee />
        </div>
      </section>

      {/* ======== P3: Video Section ======== */}
      <SpotlightEffect>
        <section className="home-video" data-nav-theme="dark">
        <div className="home-video__antigravity" aria-hidden="true">
          <Particles
            particleColors={["#ee3a47", "#f5c03f", "#e85a55"]}
            particleCount={200}
            particleSpread={10}
            speed={0.1}
            particleBaseSize={100}
            moveParticlesOnHover={false}
            alphaParticles={false}
            disableRotation
          />
        </div>
        <FadeIn>
          <div className="home-video__text">
            <h2 className="home-video__title">
              <span className="home-video__accent">{t.cultivateAccent}</span>{t.cultivateTitleSuffix}
            </h2>
            <p className="home-video__subtitle">
              {t.cultivateSubtitle}
            </p>
          </div>
        </FadeIn>
        <div className="home-video__player">
          <img
            src="/images/company-admin/company-admin-dashboard1.png"
            alt={t.dashboardAlt}
          />
        </div>
      </section>
      </SpotlightEffect>

      {/* ======== P4-P6: How + Features + Stats (one seamless section) ======== */}
      <section className="home-features">
        <div className="section">
          {/* Section header */}
          <FadeIn>
            <div className="section-header">
              <p className="section-label">{t.mainFeaturesLabel}</p>
              <h2 className="section-title">
                {t.featuresTitle}
              </h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                {t.featuresSubtitle}
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="home-how__cta">
              <button className="btn-accent" onClick={handleRequestDemo}>
                {t.requestForFreeDemo}
              </button>
            </div>
          </FadeIn>

          {/* 4 feature cards */}
          <GSAPReveal stagger={0.12} className="features-grid-lark">
            {homeFeatures.map((feature, idx) => (
              <div key={idx} className="feature-card-lark">
                <div className="feature-card-lark__image">
                  <img src={process.env.PUBLIC_URL + feature.image} alt={feature.title} />
                </div>
                <h3 className="feature-card-lark__title">{feature.title}</h3>
                <p className="feature-card-lark__desc">{feature.description}</p>
                <span className="feature-card-lark__link">
                  {feature.link} <span className="feature-card-lark__arrow">&rarr;</span>
                </span>
              </div>
            ))}
          </GSAPReveal>

          {/* Stats banner */}
          <FadeIn delay={400}>
            <div className="features-bottom-banner">
              <div className="features-bottom-banner__text">
                <h3 className="features-bottom-banner__title">{homeBottomFeature.title}</h3>
                <p className="features-bottom-banner__desc">{homeBottomFeature.description}</p>
                <button className="features-bottom-banner__btn" onClick={handleRequestDemo}>
                  {homeBottomFeature.link}
                </button>
              </div>
              <div className="features-bottom-banner__divider" />
              <div className="features-bottom-banner__stats">
                {homeBottomFeature.stats.map((stat, idx) => (
                  <div key={idx} className="features-bottom-banner__stat">
                    <div className="features-bottom-banner__stat-value"><CountUp value={stat.value} /></div>
                    <div className="features-bottom-banner__stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ======== Product Grid (Lark-style) ======== */}
      <section className="home-products">
        <div className="home-products__container">
          {/* Floating image on the right */}
          <div className="home-products__float-img">
            <img src={process.env.PUBLIC_URL + '/images/All features.png'} alt={t.allFeaturesImageAlt} />
          </div>

          {/* Centered text at top */}
          <div className="home-products__header">
            <p className="section-label">{t.allFeaturesLabel}</p>
            <h2 className="section-title">
              {t.allFeaturesTitle}
            </h2>
            <button className="btn-accent home-products__cta" onClick={handleRequestDemo}>
              {t.getStartedForFree}
            </button>
          </div>

          {/* Feature cards grid */}
          <div className="home-products__grid" ref={gridRef} onScroll={handleGridScroll}>
            {productFeatures.map((feature, i) => (
              <FadeIn key={feature.id} delay={i * 60}>
                <div
                  className="home-product-card"
                  onClick={() => onNavigate && onNavigate('feature-' + feature.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigate && onNavigate('feature-' + feature.id);
                    }
                  }}
                >
                  <div className="home-product-card__icon" style={{ background: feature.iconBg }}>
                    {feature.icon}
                  </div>
                  <h3 className="home-product-card__title">{feature.title}</h3>
                  <p className="home-product-card__desc">{feature.shortDesc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <div className="home-products__progress">
            <div
              className="home-products__progress-fill"
              style={{ transform: `scaleX(${scrollProgress})` }}
            />
          </div>
        </div>
      </section>

      {/* ======== Testimonials + CTA (merged) ======== */}
      <section className="home-testimonials-cta">
        <div className="section">
          <GSAPReveal>
            <TestimonialCarousel />
          </GSAPReveal>
        </div>
        <FadeIn>
          <CTASection
            title={t.ctaTitle}
            subtitle={t.ctaSubtitle}
            buttonText={t.ctaButton}
          />
        </FadeIn>
      </section>
    </div>
  );
}
